<template>
    <footer role="contentinfo" class="pro-footer">
        <div class="container">
            <nav class="links desktop" :aria-label="$t('a11y-labels.footer.nav')">
                <div class="column">
                    <pro-footer-section :section="sections.discover" />
                    <pro-footer-section :section="sections.showcase" />
                </div>

                <div class="column">
                    <pro-footer-section :section="sections.connect" />
                </div>

                <div class="column">
                    <pro-footer-section :section="sections.discussions" />
                </div>

                <div class="column">
                    <pro-footer-section :section="sections.support" />
                    <pro-footer-section :section="sections.handbooks" />
                    <!-- <pro-footer-section :section="sections.account" /> -->
                </div>
            </nav>
            <nav class="links mobile" :aria-label="$t('a11y-labels.footer.nav')">
                <div class="column">
                    <pro-footer-section :section="sections.discover" />
                    <pro-footer-section :section="sections.connect" />
                    <pro-footer-section :section="sections.handbooks" />
                    <pro-footer-section :section="sections.showcase" />
                </div>

                <div class="column">
                    <pro-footer-section :section="sections.discussions" />
                    <pro-footer-section :section="sections.marketplace" />
                    <pro-footer-section :section="sections.support" />
                    <!-- <pro-footer-section :section="sections.account" /> -->
                </div>
            </nav>

            <nav class="legals" :aria-label="$t('a11y-labels.footer.legal')">
                <ul>
                    <li>
                        <ProLink :to="{ name: 'terms-conditions' }" :title="$t('footer.terms_conditions')">{{ $t("footer.terms_conditions") }}</ProLink>
                    </li>
                    <li>
                        <ProLink :to="{ name: 'privacy-policy' }" :title="$t('footer.privacy_policy')">{{ $t("footer.privacy_policy") }}</ProLink>
                    </li>
                    <li>
                        <ProLink :to="{ name: 'cookies-policy' }" :title="$t('footer.cookies_policy')">{{ $t("footer.cookies_policy") }}</ProLink>
                    </li>
                    <li>
                        <ProLink :to="{ name: 'careers' }" :title="$t('footer.careers')">{{ $t("footer.careers") }} </ProLink>
                    </li>
                    <li>
                        <LocaleDropdown class="footer-locale-dropdown" />
                    </li>
                </ul>
                <div class="copyright">
                    <span class="acknow-of-country">{{ $t("footer.aoc") }}</span> <br />
                    {{ $t("footer.copyright", { year: new Date().getFullYear() }) }}
                </div>
            </nav>
        </div>
    </footer>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ProFooterSection from "./ProFooterSection";
import LocaleDropdown from "@/components/LocaleDropdown";

export default {
    components: { ProFooterSection, LocaleDropdown },

    data: function () {
        return {};
    },
    computed: {
        ...mapGetters({
            isLoggedIn: "auth/isLoggedIn",
            user: "auth/getUser",
        }),

        sections() {
            return {
                discover: [
                    {
                        title: this.$t("footer.discover"),
                        to: { name: "index" },
                    },
                    {
                        title: this.$t("footer.discover_whats_new"),
                        to: { name: "ipad-whats-new" },
                    },
                    {
                        title: this.$t("footer.discover_ipad"),
                        to: { name: "ipad" },
                    },
                    {
                        title: this.$t("footer.discover_pocket"),
                        to: { name: "pocket" },
                    },
                    {
                        title: this.$t("footer.discover_insight"),
                        to: { name: "insight" },
                    },
                    {
                        title: this.$t("footer.discover_art_prize"),
                        to: { name: "prize-2020" },
                    },
                    {
                        title: "Learn with Procreate",
                        to: "https://education.procreate.com",
                        external: true,
                    },
                ],

                showcase: [
                    {
                        title: this.$t("footer.showcase"),
                        to: "https://folio.procreate.com",
                        external: true,
                    },
                    {
                        title: this.$t("footer.showcase_featured"),
                        to: "https://folio.procreate.com",
                        external: true,
                    },
                    {
                        title: this.$t("footer.showcase_popular"),
                        to: "https://folio.procreate.com/showcase/popular",
                        external: true,
                    },
                    {
                        title: this.$t("footer.showcase_recent"),
                        to: "https://folio.procreate.com/showcase/recent",
                        external: true,
                    },
                ],

                connect: [
                    {
                        title: this.$t("footer.connect"),
                    },
                    {
                        title: this.$t("footer.connect_instagram"),
                        alt: this.$t("footer.connect_instagram_alt"),
                        to: "https://instagram.com/Procreate",
                        external: true,
                        fathomEvent: "Q04ENODX",
                    },
                    {
                        title: this.$t("footer.connect_youtube"),
                        alt: this.$t("footer.connect_youtube_alt"),
                        to: this.$t("footer.connect_youtube_url"),
                        external: true,
                        fathomEvent: "ZLZOYHRT",
                    },
                    {
                        title: this.$t("footer.connect_twitter"),
                        alt: this.$t("footer.connect_twitter_alt"),
                        to: this.$t("footer.connect_twitter_url"),
                        external: true,
                        fathomEvent: "BOVNMAYK",
                    },
                    {
                        title: this.$t("footer.connect_facebook"),
                        alt: this.$t("footer.connect_facebook_alt"),
                        to: "https://www.facebook.com/procreate",
                        external: true,
                        fathomEvent: "0XH5I13X",
                    },
                    {
                        title: this.$t("footer.connect_weibo"),
                        alt: this.$t("footer.connect_weibo_alt"),
                        to: "https://www.weibo.com/procreateofficial",
                        external: true,
                        fathomEvent: "AZVADERQ",
                    },
                    {
                        title: this.$t("footer.connect_red"),
                        alt: this.$t("footer.connect_red_alt"),
                        to: "https://www.xiaohongshu.com/user/profile/5d0712d4000000001000a6fc",
                        external: true,
                        fathomEvent: "WPYOMJAS",
                    },
                    {
                        title: this.$t("footer.connect_line"),
                        alt: this.$t("footer.connect_line_alt"),
                        to: "https://line.me/R/ti/p/@procreate?from=page",
                        external: true,
                        fathomEvent: "UPUD80MU",
                    },
                ],

                discussions: [
                    {
                        title: this.$t("footer.discussions"),
                        to: "https://folio.procreate.com/discussions",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_ideas"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_ideas")}`,
                        to: "https://folio.procreate.com/discussions/3",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_help"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_help")}`,
                        to: "https://folio.procreate.com/discussions/4",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_marketplace"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_marketplace")}`,
                        to: "https://folio.procreate.com/discussions/12",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_resources"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_resources")}`,
                        to: "https://folio.procreate.com/discussions/10",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_general"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_general")}`,
                        to: "https://folio.procreate.com/discussions/5",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_stories"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_stories")}`,
                        to: "https://folio.procreate.com/discussions/8",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_advice"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_advice")}`,
                        to: "https://folio.procreate.com/discussions/7",
                        external: true,
                    },
                    {
                        title: this.$t("footer.discussions_hardware"),
                        alt: `${this.$t("footer.discussions")} - ${this.$t("footer.discussions_hardware")}`,
                        to: "https://folio.procreate.com/discussions/11",
                        external: true,
                    },
                ],

                support: [
                    {
                        title: this.$t("footer.support"),
                        to: { name: "support" },
                    },
                    {
                        title: this.$t("footer.support_faq"),
                        alt: this.$t("footer.support_faq_alt"),
                        to: { name: "faq" },
                    },
                    {
                        title: this.$t("footer.support_beta"),
                        to: { name: "support-beta" },
                    },
                    {
                        title: this.$t("footer.support_press"),
                        to: { name: "press" },
                    },
                ],

                handbooks: [
                    {
                        title: this.$t("footer.handbooks"),
                    },
                    {
                        title: this.$t("footer.handbooks_ipad"),
                        to: { name: "handbook-product-all", params: { product: "procreate", pathMatch: "introduction" } },
                    },
                    {
                        title: this.$t("footer.handbooks_pocket"),
                        to: { name: "handbook-product-all", params: { product: "pocket", pathMatch: "introduction" } },
                    },
                ],

                account: [
                    {
                        title: this.$t("footer.account"),
                    },
                    ...(() => {
                        if (this.isLoggedIn) {
                            return [
                                {
                                    title: this.$t("global.logout"),
                                    action: this.logout,
                                },
                                {
                                    title: this.$t("global.account_settings"),
                                    to: "https://folio.procreate.com/settings",
                                    external: true,
                                },
                            ];
                        }

                        return [
                            {
                                title: this.$t("global.login"),
                                action: this.login,
                            },
                            {
                                title: this.$t("footer.account_create"),
                                action: this.register,
                            },
                        ];
                    })(),
                ],
            };
        },
    },

    methods: {
        ...mapActions({
            login: "auth/openLoginModal",
            logout: "auth/logout",
            register: "auth/openRegisterModal",
        }),
    },
};
</script>
